import { Bot, CalendarDays, Check, Clock3, CreditCard, RefreshCw, ShieldCheck, TimerReset } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { chooseFreeAgentBuildRequest, createAgentBuildRequestCheckout, getPublicAgentBuildRequest } from '../../services/agent-build-request';

const STATUS_LABELS = {
  PENDING: 'Recebido',
  IN_REVIEW: 'Em análise',
  NEEDS_INFORMATION: 'Aguardando informações',
  APPROVED: 'Aprovado',
  IN_PROGRESS: 'Em desenvolvimento',
  COMPLETED: 'Concluído',
  REJECTED: 'Não aprovado',
  CANCELED: 'Cancelado',
};

const STATUS_HELPERS = {
  PENDING: 'Seu briefing chegou e foi registrado com sucesso.',
  IN_REVIEW: 'Nossa equipe está analisando o escopo do agente.',
  NEEDS_INFORMATION: 'Precisamos esclarecer alguns pontos. Confira o WhatsApp ou e-mail informado no briefing.',
  APPROVED: 'O escopo foi aprovado e seguirá para desenvolvimento.',
  IN_PROGRESS: 'Seu agente está sendo preparado pela nossa equipe.',
  COMPLETED: 'O trabalho deste atendimento foi concluído.',
  REJECTED: 'A solicitação não seguirá adiante neste momento.',
  CANCELED: 'Este atendimento foi cancelado.',
};

function unwrap(response) {
  return response?.data ?? response;
}

export default function AgentRequestTracking() {
  const { trackingId } = useParams();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState('');
  const [actionError, setActionError] = useState('');

  const load = useCallback(
    async (silent = false) => {
      if (silent) setRefreshing(true);
      else setLoading(true);
      try {
        const response = await getPublicAgentBuildRequest(trackingId);
        setRequest(unwrap(response));
        setError('');
      } catch (requestError) {
        if (!silent) {
          setError(requestError?.status === 410 ? 'Este link de acompanhamento expirou.' : requestError?.data?.message || 'Não foi possível encontrar este atendimento.');
        }
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [trackingId]
  );

  useEffect(() => {
    const previousTitle = document.title;
    const robots = document.createElement('meta');
    robots.name = 'robots';
    robots.content = 'noindex,nofollow,noarchive';
    document.head.appendChild(robots);
    document.title = 'Acompanhar atendimento | Verboo';
    load();
    const interval = window.setInterval(() => load(true), 30000);
    return () => {
      window.clearInterval(interval);
      robots.remove();
      document.title = previousTitle;
    };
  }, [load]);

  const openCheckout = async () => {
    setActionLoading('checkout');
    setActionError('');
    try {
      const response = await createAgentBuildRequestCheckout(trackingId);
      const data = unwrap(response);
      window.location.assign(data.checkoutUrl);
    } catch (requestError) {
      setActionError(requestError?.data?.message || 'Não foi possível abrir o pagamento agora.');
      setActionLoading('');
    }
  };

  const chooseFree = async () => {
    if (!window.confirm('Mudar definitivamente para a modalidade Lenta (gratuita)? O pagamento atual será cancelado.')) return;
    setActionLoading('free');
    setActionError('');
    try {
      const response = await chooseFreeAgentBuildRequest(trackingId);
      setRequest(unwrap(response));
    } catch (requestError) {
      setActionError(requestError?.data?.message || 'Não foi possível mudar a modalidade.');
    } finally {
      setActionLoading('');
    }
  };

  if (loading)
    return (
      <Page>
        <div className="mx-auto h-96 max-w-3xl animate-pulse rounded-[2rem] border border-white/10 bg-white/[0.035]" />
      </Page>
    );
  if (!request)
    return (
      <Page>
        <div className="mx-auto max-w-xl rounded-[2rem] border border-rose-400/20 bg-rose-400/[0.06] p-8 text-center">
          <Clock3 className="mx-auto text-rose-300" size={34} />
          <h1 className="mt-5 text-2xl font-semibold">Acompanhamento indisponível</h1>
          <p className="mt-3 text-sm text-white/50">{error}</p>
        </div>
      </Page>
    );

  return (
    <Page>
      <div className="mx-auto max-w-3xl">
        <header className="mb-8 flex items-center justify-between border-b border-white/[0.07] pb-5">
          <div className="flex items-center gap-3">
            <Bot className="text-fuchsia-400" size={21} />
            <span className="text-sm font-semibold">
              Verboo <span className="text-white/35">Studio</span>
            </span>
          </div>
          <span className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-white/30">
            <ShieldCheck size={13} /> acompanhamento seguro
          </span>
        </header>

        <section className="rounded-[2rem] border border-white/10 bg-[#111016]/90 p-6 shadow-2xl backdrop-blur-xl sm:p-10">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.23em] text-fuchsia-300">Status do atendimento</p>
              <h1 className="mt-3 text-3xl font-semibold tracking-tight sm:text-5xl">{request.canRetryPayment ? 'Aguardando pagamento' : STATUS_LABELS[request.status] || request.status}</h1>
              <p className="mt-4 max-w-xl text-sm leading-7 text-white/50">{request.canRetryPayment ? 'Seu briefing está salvo, mas ainda não entrou na fila prioritária. Conclua o pagamento ou escolha a fila gratuita.' : STATUS_HELPERS[request.status]}</p>
            </div>
            <Button type="button" variant="outline" leftIcon={RefreshCw} disabled={refreshing} className={refreshing ? '[&_svg]:animate-spin' : ''} onClick={() => load(true)}>
              Atualizar
            </Button>
          </div>
          <dl className="mt-9 grid gap-4 border-t border-white/[0.07] pt-7 sm:grid-cols-2">
            <Info label="Agente" value={request.agentName} />
            <Info label="Empresa" value={request.companyName} />
            <Info label="Solicitação recebida" value={formatDate(request.submittedAt)} />
            <Info label="Última atualização" value={formatDate(request.updatedAt)} />
          </dl>
        </section>

        <section className="mt-6 rounded-[2rem] border border-white/10 bg-[#111016]/75 p-6 sm:p-10">
          <div className="flex items-start gap-4">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-fuchsia-400/20 bg-fuchsia-400/[0.08] text-fuchsia-300">{request.serviceLevel === 'PRIORITY' ? <TimerReset size={20} /> : <Check size={20} />}</span>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/30">Modalidade</p>
              <h2 className="mt-1 text-lg font-semibold">{request.serviceLevel === 'PRIORITY' ? 'Rápida · fila prioritária' : 'Lenta · fila gratuita'}</h2>
              <p className="mt-2 text-xs leading-5 text-white/45">{request.serviceLevel === 'PRIORITY' ? `${formatMoney(request.priorityFeeAmountCents, request.priorityFeeCurrency)} · ${paymentLabel(request.paymentStatus)}` : 'Sem cobrança · projeto na fila geral da equipe'}</p>
            </div>
          </div>
          {new URLSearchParams(window.location.search).get('checkout') === 'cancelled' && request.canRetryPayment && <p className="mt-5 rounded-xl border border-amber-300/15 bg-amber-300/[0.06] px-4 py-3 text-xs text-amber-100/70">O pagamento foi cancelado e sua solicitação continua salva, fora da fila.</p>}
          {actionError && <p className="mt-5 rounded-xl border border-rose-400/20 bg-rose-400/[0.07] px-4 py-3 text-xs text-rose-300">{actionError}</p>}
          {request.canRetryPayment && (
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <Button type="button" leftIcon={CreditCard} loading={actionLoading === 'checkout'} onClick={openCheckout}>
                Pagar prioridade com Stripe
              </Button>
              <Button type="button" variant="outline" loading={actionLoading === 'free'} onClick={chooseFree}>
                Migrar para Lenta (gratuita)
              </Button>
            </div>
          )}
        </section>

        <section className="mt-6 rounded-[2rem] border border-white/10 bg-[#111016]/75 p-6 sm:p-10">
          <h2 className="text-lg font-semibold">Histórico</h2>
          <div className="mt-7 space-y-0">
            {(request.statusHistory || []).map((event, index, history) => (
              <div key={event.id} className="relative flex gap-4 pb-8 last:pb-0">
                {index < history.length - 1 && <span className="absolute left-[15px] top-8 h-[calc(100%-1.25rem)] w-px bg-white/10" />}
                <span className="relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-fuchsia-400/30 bg-fuchsia-400/10 text-fuchsia-300">
                  <Check size={14} />
                </span>
                <div className="pt-0.5">
                  <p className="text-sm font-semibold text-white">{STATUS_LABELS[event.status] || event.status}</p>
                  <p className="mt-1 flex items-center gap-2 text-xs text-white/35">
                    <CalendarDays size={12} /> {formatDate(event.occurredAt)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>
        <p className="mt-7 text-center text-xs leading-6 text-white/30">As atualizações também são enviadas ao WhatsApp e e-mail informados no briefing. Para tirar dúvidas, responda diretamente pelo WhatsApp.</p>
      </div>
    </Page>
  );
}

function Page({ children }) {
  return (
    <main className="min-h-screen bg-[#08070b] px-4 py-8 text-white sm:px-7 sm:py-12">
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(circle_at_18%_12%,rgba(173,52,254,0.13),transparent_31%),radial-gradient(circle_at_82%_82%,rgba(92,65,255,0.09),transparent_35%)]" /> <div className="relative z-10">{children}</div>
    </main>
  );
}

function Info({ label, value }) {
  return (
    <div className="rounded-xl border border-white/[0.07] bg-white/[0.025] p-4">
      <dt className="text-[10px] uppercase tracking-[0.15em] text-white/30">{label}</dt>
      <dd className="mt-2 text-sm font-medium text-white/75">{value}</dd>
    </div>
  );
}

function formatDate(value) {
  return new Date(value).toLocaleString('pt-BR', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function formatMoney(amount, currency = 'BRL') {
  if (amount == null) return 'Valor não informado';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency }).format(amount / 100);
}

function paymentLabel(status) {
  return (
    {
      PENDING: 'Pagamento pendente',
      FAILED: 'Pagamento não concluído',
      EXPIRED: 'Pagamento expirado',
      PAID: 'Pagamento confirmado',
      PARTIALLY_REFUNDED: 'Reembolso parcial registrado',
      REFUNDED: 'Reembolso registrado',
    }[status] || status
  );
}
